import { buildSelectedChannel, singleDomainRedirect } from '@jetshop/core/components/ChannelContext/utils';
// Intl
import { setupIntl } from '@jetshop/intl/config';
import { NetworkStatusProvider } from '@jetshop/ui/Loading/NetworkStatusContext';
// Emotion
import { hydrate } from 'emotion';
// Sentry
import Raven from 'raven-js';
import React from 'react';
// Cookies
import { Cookies } from 'react-cookie';
// React DOM
import { render as renderToDOM } from 'react-dom';
// Metadata
// React Loadable
import Loadable from 'react-loadable';
// React Router DOM
import { BrowserRouter } from 'react-router-dom';
import { CartIdProvider } from '../components/Cart/CartIdContext';
import NostoProvider from '../components/nosto/Provider';
import AddwishProvider from '../components/addwish/AddwishProvider';
import { createClient, setChannel } from './apollo';
import channelsQuery from './ChannelsQuery.gql';
import { setUID } from './utils/uid';
import SharedTree from './SharedTree';
import createIntlOptions from './utils/createIntOptions';
import logWarningInDev from '../helpers/logWarningInDev';
// Instantiate this here, since we need to read cookies outside of React.
const cookies = new Cookies();
function wrapTree(tree, wrappers) {
    return wrappers.reduce((wrappedTree, wrap) => wrap(wrappedTree), tree);
}
const startClient = (tree, config) => {
    if (!config.apolloConfig.channelsQuery) {
        logWarningInDev(true, `No channelsQuery was provided in the apolloConfig, which is a deprecated behaviour. Please update your shop.config.js to include the ChannelsQuery to avoid breaking in the next major release. See https://gitlab.jetshop.se/jetshop/flight/blob/master/packages/template-trend/src/ChannelsQuery.gql for a reference implementation`);
    }
    Raven.config(config.sentryClientDSN, {
        tags: { BUILD_TARGET: 'client' },
        ignoreErrors: [
            'PersistedQueryNotFound',
            /^(.*?(\bMissingRoute\b)[^$]*)/g,
            'PERSISTED_QUERY_NOT_FOUND'
        ],
        ignoreUrls: [/embed\.tawk\.to/]
    }).install();
    const { apolloConfig } = config;
    // Read locale from cookie and make sure we've loaded translations before we start rendering
    const { client, NetworkStatusNotifier } = createClient({ apolloConfig });
    client
        .query({
        query: config.apolloConfig.channelsQuery || channelsQuery
    })
        .then(({ data }) => {
        const channels = data.channels;
        const selectedChannel = buildSelectedChannel({
            url: window.location.href,
            cookies,
            channels,
            singleDomainMode: config.singleDomainMode
        });
        setChannel(selectedChannel);
        // Used only for development since the server will do this in production
        if (process.env.NODE_ENV !== 'production' && config.singleDomainMode) {
            const redirectUrl = singleDomainRedirect({
                url: window.location.href,
                selectedChannel,
                channels
            });
            if (redirectUrl) {
                window.history.replaceState(null, null, redirectUrl);
            }
        }
        const channelOverrides = config.channelOverrides
            ? config.channelOverrides[selectedChannel.name]
            : {};
        let { intl } = Object.assign({}, config, channelOverrides);
        const nostoAccountID = selectedChannel.settings.nostoAccountId || config.nostoAccountID;
        const intlOptions = createIntlOptions({
            selectedChannel,
            options: intl.options
        });
        return setupIntl({
            translations: intl.translations,
            locale: selectedChannel.language.culture,
            defaultLocale: intl.defaultLocale,
            options: intlOptions
        }).then(() => {
            const activeBreakpoint = window.__ACTIVE_BREAKPOINT__;
            const emotionIds = window.__EMOTION_IDS__;
            emotionIds && hydrate(emotionIds);
            const releware = {
                enabled: config.relewareEnabled,
                baseUrl: document.domain,
                uid: config.relewareEnabled ? setUID(cookies) : null
            };
            const wrappedTree = (React.createElement(SharedTree, { activeBreakpoint: activeBreakpoint, config: config, cookies: cookies, releware: releware, client: client, selectedChannel: selectedChannel, channels: channels, Router: BrowserRouter },
                React.createElement(NetworkStatusNotifier, { render: (value) => {
                        return (React.createElement(CartIdProvider, null,
                            React.createElement(NetworkStatusProvider, { value: value }, wrapTree(tree, [
                                wrappedTree => nostoAccountID ? (React.createElement(NostoProvider, { id: nostoAccountID }, wrappedTree)) : (wrappedTree),
                                wrappedTree => config.addwishAccountID ? (React.createElement(AddwishProvider, null, wrappedTree)) : (wrappedTree)
                            ]))));
                    } })));
            return Loadable.preloadReady().then(() => wrappedTree);
        });
    })
        .then((tree) => {
        renderToDOM(tree, document.getElementById('root'));
    });
};
const clientBoot = (tree, config) => {
    // If the polyFillsLoaded flag is set to true then we know that the polyFills have been loaded and we can start the client.
    if (window.polyFillsLoaded ||
        process.env.NODE_ENV !== 'production') {
        startClient(tree, config);
    }
    else {
        // If the polyfills have not been loaded we want to replace the bootShop to start the client instead so that once the polyfills are loaded, bootShop will start the client.
        window.bootShop = () => startClient(tree, config);
    }
};
export default clientBoot;
//# sourceMappingURL=client.js.map